import React, { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import CustomInput from "../../../../common/CustomInput";
import {
  alcoholEnum,
  smokingEnum,
  exerciseEnum,
  recreationalDrugsEnum,
  dietEnum,
  caffeineEnum,
  exerciseTypeEnum,
} from "./data";
import { useSelector, useDispatch } from "react-redux";
import {
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import { useParams } from "react-router-dom";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import SmokingCessationAlert from "../../alerts/SmokingCessationAlert";
import SubNavigation from "../../common/sub-navigation";
import { scrollX, scrollY, calculateAgeYear } from "../../../../../helpers/functions";
import { upsertSmokingAlert, removeSmokingAlert } from "../../../../../redux/modules/patient/actions";

const LifeStyle = () => {
  //State Variables
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();

  //Local Variables
  const [alcoholVal, setAlcoholVal] = useState(null);
  const [smokingVal, setSmokingVal] = useState(null);
  const [caffeineVal, setCaffeineVal] = useState(null);
  const [exerciseVal, setExerciseVal] = useState(null);
  const [smokingCessation, setSmokingCessation] = useState(false);
  const [culturalComments, setCulturalComments] = useState(null);
  const [drugComments, setDrugComments] = useState(null);
  const [lifestyleComments, setLifestyleComments] = useState(null);
  const [completeSubStep, setCompleteSubStep] = useState(false);

  //Multiselect Variables
  const [drugVals, setDrugVals] = useState([]);
  const [dietVals, setDietVals] = useState([]);
  const [exerciseTypeVals, setExerciseTypeVals] = useState([]);
  const [culturalVals, setculturalVals] = useState([]);
  const [openSmokingCessationDialog, setOpenSmokingCessationDialog] = useState(false);
  const [lifeStyleObj, setLifeStyleObj] = useState(null);

  // get patient from redux to determine age
  const { patient } = useSelector((state) => state.patients);
  const isUnder12 = patient?.dateOfBirth && calculateAgeYear(patient.dateOfBirth) < 12;

  // Do not render this UI for patients under 12
  if (isUnder12) return null;

  const setNotApplicable = () => {
    setAlcoholVal("Not Assessed");
    setCaffeineVal("Not Assessed");
    setSmokingVal("Not Assessed");
    setDrugVals(["Not Assessed"]);
    setDietVals(["Not Assessed"]);
    setExerciseVal("Not Assessed");
    setExerciseTypeVals(["Not Assessed"]);
    setDrugComments(null);
    setCulturalComments(null);
    setculturalVals(["Not Assessed"]);
    setSmokingCessation(false);
    setLifestyleComments("Not Assessed due to recently completed or not clinically indicated.");
    setTimeout(() => {
      scrollX("life-style", 1200);
    }, 300);
  };
  //state management
  useEffect(() => {
    let obj =
      clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
        ? clinicalServiceObj.initialAssessment.initialAssessmentForm
            .clinicalAssessment
        : clinicalServiceObj.initialAssessment.initialAssessmentForm
            .healthAndWellness;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      obj !== null &&
      obj !== undefined
    ) {
      setAlcoholVal(obj?.alcohol);
      setCaffeineVal(obj?.caffeine);
      setSmokingVal(obj?.smoking);
      setSmokingCessation(obj?.smokingCessationIndicator);
      setDietVals(obj?.diet || []);
      setDrugVals(obj?.recreationalDrugs || []);
      setDrugComments(obj?.recreationalDrugsComments);
      setExerciseVal(obj?.exercise);
      setExerciseTypeVals(obj?.exerciseTypes || []);
      setculturalVals(obj?.culturalFactors || []);
      setCulturalComments(obj?.culturalFactorsComments);
      if (obj?.lifestyle != null && (lifestyleComments == null)) {
        setLifestyleComments(obj.lifestyle);
      }

      if (obj?.exercise && obj?.exercise?.length > 0) {
        scrollX("life-style", 480 * 5);
      }
    }
  }, [clinicalServiceObj]);

  const drugMultiSelection = (val) => {
    if (val === "None" || val === "Not Assessed") {
      !drugVals.includes(val) ? setDrugVals([val]) : setDrugVals([]);
    } else {
      if (drugVals.includes("None") || drugVals.includes("Not Assessed")) {
        setDrugVals([val]);
      } else {
        drugVals.includes(val)
          ? setDrugVals(drugVals.filter((item) => item !== val))
          : setDrugVals([...drugVals, val]);
      }
    }
  };

  const dietMultiSelection = (val) => {
    if (val === "None" || val === "Not Assessed") {
      !dietVals.includes(val) ? setDietVals([val]) : setDietVals([]);
    } else {
      if (dietVals.includes("None") || dietVals.includes("Not Assessed")) {
        setDietVals([val]);
      } else {
        dietVals.includes(val)
          ? setDietVals(dietVals.filter((item) => item !== val))
          : setDietVals([...dietVals, val]);
      }
    }
  };

  const exerciseTypeMultiSelection = (val) => {
    if (val === "None" || val === "Not Assessed") {
      !exerciseTypeVals.includes(val)
        ? setExerciseTypeVals([val])
        : setExerciseTypeVals([]);
    } else {
      if (exerciseTypeVals.includes("None") || exerciseTypeVals.includes("Not Assessed")) {
        setExerciseTypeVals([val]);
      } else {
        exerciseTypeVals.includes(val)
          ? setExerciseTypeVals(exerciseTypeVals.filter((item) => item !== val))
          : setExerciseTypeVals([...exerciseTypeVals, val]);
      }
    }
  };

  //Life Style Models

  //Move Back and Forth
  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);

      if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
        const updatedClinicalServiceObj = {
          clinicalAssessment: {
            alcohol: alcoholVal,
            caffeine: caffeineVal,
            smoking: smokingVal,
            recreationalDrugs: drugVals,
            recreationalDrugsComments: drugComments,
            diet: dietVals,
            smokingCessationIndicator: smokingCessation,
            exercise: exerciseVal,
            exerciseTypes: exerciseTypeVals,
            culturalFactors: culturalVals,
            culturalFactorsComments: culturalComments,
            lifestyle: lifestyleComments,
          },
          currentStep: nextStep,
          currentSubStep: nextSubStep,
          serviceSteps: clinicalServiceSteps
        };
        dispatch(updateClinicalService(serviceId, updatedClinicalServiceObj));
      } else if (
        clinicalServiceName === ClinicalServiceTypes.MedicationReview
      ) {
        const updatedClinicalServiceObj = {
          healthAndWellness: {
            alcohol: alcoholVal,
            caffeine: caffeineVal,
            smoking: smokingVal,
            recreationalDrugs: drugVals,
            recreationalDrugsComments: drugComments,
            diet: dietVals,
            smokingCessationIndicator: smokingCessation,
            exercise: exerciseVal,
            exerciseTypes: exerciseTypeVals,
            culturalFactors: culturalVals,
            culturalFactorsComments: culturalComments,
            lifestyle: lifestyleComments,
          },
          currentStep: nextStep,
          currentSubStep: nextSubStep,
          serviceSteps: clinicalServiceSteps
        };
        dispatch(updateMedicationReview(serviceId, updatedClinicalServiceObj));
      }
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, -1);
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };

    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      dispatch(updateClinicalService(serviceId, updatedSteps));
    } else if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
      dispatch(updateMedicationReview(serviceId, updatedSteps));
    }
  };
  useEffect(() => {
    setLifeStyleObj({
      ...lifeStyleObj,
      alcoholVal: alcoholVal,
    });
  }, [alcoholVal]);

  useEffect(() => {
    setLifeStyleObj({
      ...lifeStyleObj,
      drugVals: drugVals,
    });
  }, [drugVals]);

  useEffect(() => {
    setLifeStyleObj({
      ...lifeStyleObj,
      caffeineVal: caffeineVal,
    });
  }, [caffeineVal]);

  useEffect(() => {
    setLifeStyleObj({
      ...lifeStyleObj,
      smokingVal: smokingVal,
    });
  }, [smokingVal]);

  useEffect(() => {
    setLifeStyleObj({
      ...lifeStyleObj,
      exerciseTypeVals: exerciseTypeVals,
      exerciseVal: exerciseVal,
    });
  }, [exerciseVal, exerciseTypeVals]);

  useEffect(() => {
    if (
      alcoholVal &&
      caffeineVal &&
      smokingVal &&
      drugVals.length > 0 &&
      (exerciseVal === "Never" || exerciseVal === "Sports" || exerciseVal === "Not Assessed")
    ) {
      setCompleteSubStep(true);
    } else if (
      alcoholVal &&
      caffeineVal &&
      smokingVal &&
      drugVals.length > 0 &&
      exerciseVal !== "Never" &&
      exerciseVal !== "Sports" &&
      exerciseVal !== "Not Assessed" &&
      exerciseTypeVals.length > 0
    ) {
      setCompleteSubStep(true);
    } else {
      setCompleteSubStep(false);
    }
  }, [
    exerciseVal,
    exerciseTypeVals,
    smokingVal,
    caffeineVal,
    drugVals,
    alcoholVal,
  ]);

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Lifestyle
                  <button className="text-[18px] font-semibold text-teal-300 pl-4">
                    <span
                      onClick={() => {
                        setNotApplicable();
                      }}
                      className="underline"
                    >
                      Select to SKIP
                    </span>{" "}
                    - If recently completed or not clinically indicated.
                  </button>
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="life-style"
              >
                {/* alcohol */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/Alcohol.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Alcohol
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {alcoholEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {alcoholEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setAlcoholVal(option.value);
                                  scrollX("life-style");
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    alcoholVal === option.value
                                      ? "bg-gray-20 border-gray-600"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        <div className="border-b border-gray-100"></div>
                        <p className="text-body-md pt-5 pb-12 font-semibold text-gray-600">
                          <a href="https://www.ccsa.ca/en/guidance-tools-resources/substance-use-and-addiction/alcohol/canadas-guidance-alcohol-and-health">
                            <u>Canada's Guide on Alcohol & Health</u>
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* caffeine */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/Caffinne.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Caffeine
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] text-gray-800">
                          <span className="font-semibold">
                            {caffeineEnum.question}
                          </span>
                          <br />
                          {caffeineEnum.subquestion}
                        </p>
                        <div className="flex flex-col gap-2">
                          {caffeineEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setCaffeineVal(option.value);
                                  scrollX("life-style");
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    caffeineVal === option.value
                                      ? "bg-gray-20 border-gray-600"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        <div className="border-b border-gray-100"></div>
                        <p className="text-body-md pt-5 pb-12 font-semibold text-gray-600">
                          <a href="https://coffeeassoc.com/coffee-and-health/caffeine/">
                            <u>Coffee Association of Canada</u>
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* smoking */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/Smoking.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Smoking
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-y-auto overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {smokingEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {smokingEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  if (option.value != "Never") {
                                    setSmokingVal(option.value);
                                    setOpenSmokingCessationDialog(true);
                                    dispatch(upsertSmokingAlert(patientId, option.value));
                                  } else {
                                    scrollX("life-style");
                                    setSmokingVal(option.value);
                                    dispatch(removeSmokingAlert(patientId));
                                  }
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      smokingVal === option.value
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* recreational drugs */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/Drugs.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Recreational Drugs
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] text-gray-800">
                          <span className="font-semibold">
                            {recreationalDrugsEnum.question}
                          </span>
                          <br />
                          {recreationalDrugsEnum.subquestion}
                        </p>
                        <div className="flex flex-col gap-2">
                          {recreationalDrugsEnum.options.map((option) => {
                            return (
                              <>
                                <button
                                  onClick={() => {
                                    drugMultiSelection(option.value);
                                    scrollX("life-style");
                                  }}
                                  className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      drugVals?.includes(option.value)
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    } ${option.value === "Other" ? "!gap-0" : ""}`}
                                >
                                  <p className="font-semibold text-body-md flex justify-start items-start">
                                    {option.header}
                                  </p>
                                  <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                    {option.subHeader}
                                  </p>
                                </button>
                                {option.value === "Other" && (
                                  <CustomInput
                                    name="comments"
                                    className="col-span-12 !h-[80px]"
                                    label=""
                                    placeholder="Please specify other products"
                                    type="text"
                                    value={drugComments}
                                    onChange={(e) => setDrugComments(e.target.value)}
                                  />
                                )}
                              </>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* diet */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/diet.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">Diet</h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] text-gray-800">
                          <span className="font-semibold">
                            {dietEnum.question}
                          </span>
                        </p>
                        <div className="flex flex-col gap-2">
                          {dietEnum.options.map((option) => {
                            return (
                              <>
                                <button
                                  onClick={() => {
                                    dietMultiSelection(option.value);
                                    scrollX("life-style");
                                  }}
                                  className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      dietVals?.includes(option.value)
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    } ${option.value === "Other" ? "!gap-0" : ""}`}
                                >
                                  <p className="font-semibold text-body-md flex justify-start items-start">
                                    {option.header}
                                  </p>
                                </button>
                              </>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* exercise */}
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/Exercise.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Exercise
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]" id="exercise-card">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {exerciseEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {exerciseEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setExerciseVal(option.value);
                                  if (option.value == "Never" || option.value == "Sports")
                                    setExerciseTypeVals([]);
                                  else  {
                                    setTimeout(() => {
                                      scrollY("exercise-card");
                                    }, 300); 
                                  }
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                  exerciseVal === option.value
                                    ? "bg-gray-20 border-gray-600"
                                    : "bg-white border-gray-100"
                                }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {exerciseVal != null &&
                          exerciseVal.length > 0 &&
                          exerciseVal != "Never" && exerciseVal != "Sports" && exerciseVal != "Not Assessed" && (
                            <>
                              <div className="border-b border-gray-100"></div>
                              <p className="text-lg h-[31px] font-semibold text-gray-800">
                                {exerciseTypeEnum.question}
                              </p>
                              <div className="flex flex-col gap-2">
                                {exerciseTypeEnum.options.map((option) => {
                                  return (
                                    <button
                                      onClick={() =>
                                        exerciseTypeMultiSelection(option.value)
                                      }
                                      className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                      text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                        exerciseTypeVals?.includes(option.value)
                                          ? "bg-gray-20 border-gray-600"
                                          : "bg-white border-gray-100"
                                      }`}
                                    >
                                      <p className="font-semibold text-body-md flex justify-start items-start">
                                        {option.header}
                                      </p>
                                      <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                        {option.subHeader}
                                      </p>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <SmokingCessationAlert
              open={openSmokingCessationDialog}
              handleClose={() => {
                setOpenSmokingCessationDialog(false);
                setSmokingCessation(false);
              }}
              confirmAction={() => {
                setOpenSmokingCessationDialog(false);
                setSmokingCessation(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px]  ${
              !completeSubStep && "opacity-40 pointer-events-none"
            }`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LifeStyle;
